import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { fetchSchema, createBelief, updateBelief } from "@/lib/api";
import { MultiSelectChips } from "@/components/FilterBar";
import { toast } from "sonner";

export function BeliefEditor({ open, onClose, belief, onSaved }) {
  const isEdit = !!belief?.id;
  const [schema, setSchema] = useState(null);
  const [form, setForm] = useState({
    belief_text: "",
    category: "",
    offer_tags: [],
    source: "",
    status: "Active",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSchema().then(setSchema).catch(() => {});
  }, []);

  useEffect(() => {
    if (belief) {
      setForm({
        belief_text: belief.belief_text || "",
        category: belief.category || "",
        offer_tags: belief.offer_tags || [],
        source: belief.source || "",
        status: belief.status || "Active",
        notes: belief.notes || "",
      });
    } else {
      setForm({
        belief_text: "",
        category: "",
        offer_tags: [],
        source: "",
        status: "Active",
        notes: "",
      });
    }
  }, [belief, open]);

  const toggleTag = (tag) => {
    setForm((f) => ({
      ...f,
      offer_tags: f.offer_tags.includes(tag)
        ? f.offer_tags.filter((t) => t !== tag)
        : [...f.offer_tags, tag],
    }));
  };

  const save = async () => {
    if (!form.belief_text.trim()) {
      toast.error("Belief text is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        belief_text: form.belief_text.trim(),
        category: form.category || null,
        offer_tags: form.offer_tags,
        source: form.source || "",
        status: form.status,
        notes: form.notes || "",
      };
      const result = isEdit
        ? await updateBelief(belief.id, payload)
        : await createBelief(payload);
      toast.success(isEdit ? "Belief updated" : "Belief created");
      onSaved?.(result);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm"
        data-testid="belief-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#141B4D] text-xl font-bold tracking-tight">
            {isEdit ? "Edit belief" : "New belief"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Belief text *
            </label>
            <textarea
              data-testid="belief-text-input"
              value={form.belief_text}
              onChange={(e) =>
                setForm({ ...form, belief_text: e.target.value })
              }
              rows={4}
              className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm text-[#141B4D] focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
                Category
              </label>
              <select
                data-testid="belief-category-select"
                value={form.category}
                onChange={(e) =>
                  setForm({ ...form, category: e.target.value })
                }
                className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              >
                <option value="">—</option>
                {(schema?.belief_categories || []).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
                Status
              </label>
              <select
                data-testid="belief-status-select"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              >
                {(schema?.belief_statuses || ["Active", "Retired"]).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Offer tags
            </label>
            <MultiSelectChips
              options={schema?.offer_tags || []}
              selected={form.offer_tags}
              onToggle={toggleTag}
              testId="belief-offer-tags"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Source
            </label>
            <input
              data-testid="belief-source-input"
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Notes
            </label>
            <textarea
              data-testid="belief-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
            />
          </div>
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            data-testid="belief-cancel-button"
            className="px-5 py-2 rounded-sm text-sm border border-[#141B4D]/20 text-[#141B4D] hover:bg-[#141B4D]/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="belief-save-button"
            className="px-5 py-2 rounded-sm text-sm bg-[#141B4D] text-[#F2E9DB] font-medium hover:bg-[#141B4D]/90 disabled:opacity-60"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create belief"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
